<template>
    <div class="scroll-bar-wrapper" :class="{active}">
        <button
            v-if="withButtons"
            :class="leftButtonClass"
            @click="scrollTo(value - buttonStep)"
        />
        <div ref="scrollBar" class="scroll-bar">
            <div ref="thumb" class="thumb" />
        </div>
        <button
            v-if="withButtons"
            :class="rightButtonClass"
            @click="scrollTo(value + buttonStep)"
        />
    </div>
</template>

<script>
import gsap from 'gsap';
import Draggable from 'gsap/Draggable';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
import responder from '@kids/lib/service/responder';

gsap.registerPlugin(Draggable, ScrollToPlugin);

export default {
    name: 'ScrollBarX',
    props: {
        active: {
            type: Boolean,
            default: false
        },
        withButtons: {
            type: Boolean,
            default: false
        },
        buttonStep: {
            type: Number,
            default: 0.2
        },
        rightButtonClass: {
            type: String,
            default: 'zoom-in'
        },
        leftButtonClass: {
            type: String,
            default: 'zoom-out'
        },
        value: {
            type: Number,
            required: true
        }
    },
    data: () => ({
        width: 0,
        thumbWidth: 0,
        scrollableWidth: 0
    }),
    computed: {
        length()
        {
            return this.width - this.thumbWidth;
        }
    },
    mounted()
    {
        [this._draggable] = Draggable.create(
            this.$refs.thumb,
            {
                type: 'x',
                bounds: this.$refs.scrollBar,
                onDrag: this.scrollUpdate,
                allowNativeTouchScrolling: false
            }
        );
        responder.on('resize', this.resize);
        this.update();
    },
    destroyed()
    {
        this._draggable.kill();
        this._draggable = null;
        responder.off('resize', this.resize);
    },
    methods: {
        update(element)
        {
            this.width = this.$refs.scrollBar.clientWidth;
            this.thumbWidth = this.$refs.thumb.clientWidth;
            if (element)
            {
                this._scrollElement = element;
            }
            if (this._scrollElement)
            {
                this.scrollableWidth =
                    this._scrollElement.scrollWidth - this._scrollElement.clientWidth;
            }
        },
        scrollUpdate()
        {
            this.$emit('input', this.getThumbPosition());
            if (!this._scrollElement) { return; }
            this._scrollElement.scrollLeft = this.value * this.scrollableWidth;
        },
        getThumbPosition()
        {
            const {length} = this;
            if (!length) { return 0; }
            return gsap.getProperty(this.$refs.thumb, 'x') / length;
        },
        scrollTo(position)
        {
            if (position === this.value) { return; }

            if (position < 0)
            {
                position = 0;
            }
            else if (position > 1)
            {
                position = 1;
            }

            this._draggable.disable();
            gsap.to(
                this.$refs.thumb,
                {
                    duration: 0.6,
                    x: position * this.length,
                    onComplete: () => this._draggable.enable()
                }
            );
            this.$emit('input', position);

            if (!this._scrollElement) { return; }

            gsap.to(
                this._scrollElement,
                {duration: 0.6, scrollTo: {x: position * this.scrollableWidth}}
            );
        },
        resize()
        {
            this.update();
            if (!this._scrollElement)
            {
                gsap.set(this.$refs.thumb, {x: this.value * this.length});
                this._draggable.update();
                return;
            }
            const position = this._scrollElement.scrollLeft / this.scrollableWidth;
            gsap.set(this.$refs.thumb, {x: position * this.length, immediateRender: true});
            this._draggable.update();
        }
    }
};
</script>

<style lang="scss" src="./ScrollBarX.scss" />
